import { getLocalStorageValue } from './localstorage'
import { getCurrentSessionGeneration } from './session'

export const connectivityPortKey = 'trainer/connectivityPort'

export function getLocalConnectivityBase() {
    const envUrl = process.env.REACT_APP_SENSORS_API || 'http://localhost:8080'

    if (typeof localStorage === 'undefined' || localStorage.getItem(connectivityPortKey) === null)
        return envUrl

    let port
    try {
        port = getLocalStorageValue(connectivityPortKey, null)
    } catch (e) {
        return envUrl
    }

    if (!Number.isInteger(port) || port < 1 || port > 65535)
        return envUrl

    try {
        const url = new URL(envUrl)
        url.port = String(port)
        return url.toString().replace(/\/$/, '')
    } catch (e) {
        return envUrl
    }
}

export function getLocalUrl(url) {
    return `${getLocalConnectivityBase()}/${url}`
}

export function getRemoteUrl(url) {
    return `${process.env.REACT_APP_DATA_API}/${url}`
}

function addAuthHeader(info, token) {
    if (!token)
        return info

    return {
        ...info,
        headers: {
            ...info.headers,
            'Authorization': `Bearer ${token}`
        }
    }
}

// Local (connectivity daemon) requests are bounded by a short abort timeout.
// The daemon serializes sensor API calls behind one mutex and can stall for
// seconds during configure/hardware handshakes or a restart; without a timeout
// a sick daemon would hang every /data poll (and its queue) for minutes.
const LOCAL_REQUEST_TIMEOUT_MS = 4000

function isLocalUrl(url) {
    const base = getLocalConnectivityBase().replace(/\/$/, '')
    return url === base || url.startsWith(`${base}/`)
}

let remoteUnauthorizedHandler = null

export function setRemoteUnauthorizedHandler(fn) {
    remoteUnauthorizedHandler = fn
}

function hasBearerHeader(options) {
    const headers = options && options.headers
    if (!headers)
        return false

    const keys = typeof headers.forEach === 'function' ? [] : Object.keys(headers)
    let authValue = null
    if (typeof headers.forEach === 'function') {
        headers.forEach((value, key) => {
            if (String(key).toLowerCase() === 'authorization')
                authValue = value
        })
    } else {
        keys.forEach(key => {
            if (String(key).toLowerCase() === 'authorization')
                authValue = headers[key]
        })
    }

    return Boolean(authValue) && String(authValue).startsWith('Bearer ')
}

export function handleRemoteAuthFailure(url, options) {
    if (isLocalUrl(url))
        return false
    if (!hasBearerHeader(options))
        return false

    if (remoteUnauthorizedHandler)
        remoteUnauthorizedHandler()

    return true
}

export function getData(url, params) {
    let local
    let controller
    let timer
    if (isLocalUrl(url)) {
        local = true
        controller = new AbortController()
        timer = setTimeout(() => controller.abort(), LOCAL_REQUEST_TIMEOUT_MS)
        params = { ...(params || {}), signal: controller.signal }
    }

    // A 401 from a request that started under an obsolete session must not
    // expire a newer session (e.g. logout → login as another user while an old
    // request is still in flight). The generation is captured here at request
    // start and compared when the response arrives.
    const sessionGeneration = getCurrentSessionGeneration()

    return fetch(url, params)
        .finally(() => { if (timer) clearTimeout(timer) })
        .then(response => {
            if (response.ok) {
                if (response.status === 204) {
                    return null
                }
                return response.json()
            }

            return response.json()
                .catch(() => ({}))
                .then(body => {
                    if (response.status === 401 && getCurrentSessionGeneration() === sessionGeneration)
                        handleRemoteAuthFailure(url, params)

                    const e = new Error((body && body.detail) || `Статус ответа сервера ${response.status}`)
                    e.status = response.status
                    e.code = body && body.code
                    throw e
                })
        })
        .catch(error => {
            // Errors raised above carry status/code (server responded). Re-throw them unchanged.
            if (error && (error.status !== undefined || error.code !== undefined)) {
                throw error
            }

            if (local && error && error.name === 'AbortError') {
                throw new Error(`Устройство не отвечает: ${url}`)
            }

            // Otherwise it is a network-level failure (e.g. browser "Failed to fetch") —
            // the server could not be reached. Convert it to a descriptive message.
            throw new Error(`Не удалось подключиться к серверу: ${url}`)
        })
}

export function postData(url, data, token) {
    let info = {
        method: 'POST'
    }

    if (data) {
        info = {
            ...info,
            body: JSON.stringify(data),
            headers: {
                'Content-Type': 'application/json'
            }
        }
    }

    return getData(url, addAuthHeader(info, token))
}

export function patchData(url, data, token) {
    let info = {
        method: 'PATCH',
        body: JSON.stringify(data),
        headers: {
            'Content-Type': 'application/json'
        }
    }

    return getData(url, addAuthHeader(info, token))
}

export function putData(url, data, token) {
    let info = {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
            'Content-Type': 'application/json'
        }
    }

    return getData(url, addAuthHeader(info, token))
}

export function deleteData(url, data, token) {
    let info = {
        method: 'DELETE'
    }

    if (data) {
        info = {
            ...info,
            body: JSON.stringify(data),
            headers: {
                'Content-Type': 'application/json'
            }
        }
    }

    return getData(url, addAuthHeader(info, token))
}